import {
	type ComboboxItem,
	Fieldset,
	Grid,
	Input,
	NumberInput,
	Select,
	Skeleton,
	TagsInput,
	TextInput,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import {
	IconBuildingStore,
	IconCalendar,
	IconCreditCard,
	IconDetails,
	IconDiscount,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { FormBranchSelector } from '../../../components/form/FormBranchSelector';
import { useBranchOptions } from '../../../util/api/commonQueries';
import { usePriceTypeOptions } from '../hooks/usePriceTypeOptions';
import { inputIconStyle } from '../../../theme/styling/iconStyling';

type UpsertPromotionFormProps = {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	form: UseFormReturnType<any, (values: any) => any>;
};

export function UpsertPromotionForm({ form }: UpsertPromotionFormProps) {
	const priceTypesQuery = usePriceTypeOptions();
	const pharmaciesQuery = useBranchOptions();

	return (
		<>
			<Fieldset>
				<Grid>
					<Grid.Col span={12}>
						<TextInput
							label="Description"
							placeholder="Insert description..."
							leftSection={<IconDetails {...inputIconStyle} />}
							key={form.key('description')}
							{...form.getInputProps('description')}
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						<DateTimePicker
							clearable
							defaultValue={dayjs().format('YYYY-MM-DD')}
							label="Start Date"
							placeholder="Pick start date..."
							leftSection={<IconCalendar {...inputIconStyle} />}
							{...form.getInputProps('startDate')}
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						<DateTimePicker
							clearable
							label="End Date"
							placeholder="Pick end date..."
							leftSection={<IconCalendar {...inputIconStyle} />}
							{...form.getInputProps('endDate')}
						/>
					</Grid.Col>

					<Grid.Col span={5}>
						<FormBranchSelector form={form} property={'branchId'} />
					</Grid.Col>
					<Grid.Col span={5}>
						<TextInput
							label={'Till Description'}
							placeholder={'Select till associated with branch'}
							key={form.key('till')}
							{...form.getInputProps('till')}
							data-cy="tillNo-input"
							leftSection={<IconCreditCard {...inputIconStyle} />}
						/>
					</Grid.Col>
					<Grid.Col span={2}>
						<NumberInput
							label={'Quantity'}
							placeholder="Insert amount..."
							fixedDecimalScale
							thousandSeparator=","
							allowDecimal={false}
							allowNegative={false}
							key={form.key('qty')}
							{...form.getInputProps('qty')}
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						<NumberInput
							label={'Discount'}
							placeholder="Insert discount..."
							fixedDecimalScale
							thousandSeparator=","
							decimalScale={2}
							allowNegative={false}
							key={form.key('discount')}
							{...form.getInputProps('discount')}
							leftSection={<IconDiscount {...inputIconStyle} />}
						/>
					</Grid.Col>
					<Grid.Col span={6}>
						{!pharmaciesQuery.isLoading ? (
							<Select
								label={'Type'}
								placeholder="Select price type..."
								data={priceTypesQuery.data?.value.flatMap((p) => {
									const comboBoxItem: ComboboxItem = {
										label: `${p.description}`,
										value: p.id.toString(),
									};

									return comboBoxItem;
								})}
								clearable
								comboboxProps={{
									transitionProps: { transition: 'pop', duration: 200 },
								}}
								key={form.key('typeId')}
								{...form.getInputProps('typeId')}
								data-cy="typeId-input"
								leftSection={<IconBuildingStore {...inputIconStyle} />}
							/>
						) : (
							<Input.Wrapper label={'Branch'}>
								<Skeleton height={8} mt={6} width="100%" radius={'xl'} h={25} />
							</Input.Wrapper>
						)}
					</Grid.Col>
					<Grid.Col>
						<TagsInput
							label="Days Associated with Promotion"
							placeholder="Select days..."
							data={[
								'Monday',
								'Tuesday',
								'Wednesday',
								'Thursday',
								'Friday',
								'Saturday',
								'Sunday',
							]}
							key={form.key('days')}
							{...form.getInputProps('days')}
							acceptValueOnBlur
						/>
					</Grid.Col>
				</Grid>
			</Fieldset>
		</>
	);
}
